'use client';

import React, { useEffect, useState } from 'react';

import { useIntersectionObserver } from '@repo/react-hooks/useIntersectionObserver';
import { H1 } from '@repo/ui-kit/components/core/Typography';
import { themeClasses } from '@repo/ui-kit/css/utils';

import { MediaElement } from 'strapi/component/shared/mediaElement';
import { FlexColumn } from 'styles/layouts';

import { HideDesktop, HideMobile } from '../../../styles/queries';
import { LandingButton } from '../landingButton';
import { ComponentContainer, ComponentWrapper } from '../shared/styles';

import { durationMs } from './config';
import { Step as StepComponent } from './Step/Step';
import { BackgroundWrapper, SectionsWrapper } from './styles';
import { StepsProps } from './types';

const analyticsMeta = { componentName: 'steps' };

export const Steps: React.FC<StepsProps> = ({
  steps,
  title,
  button,
  backgroundGradient,
}) => {
  const { inView } = useIntersectionObserver({ triggerOnce: true });
  const [currentStep, setCurrentStep] = useState(0);
  const currentSlide = steps[currentStep];

  useEffect(() => {
    if (!steps?.length && !inView) {
      return;
    }

    const startInterval = setInterval(() => {
      setCurrentStep((s) => {
        if (s >= steps.length - 1) {
          return 0;
        } else {
          return s + 1;
        }
      });
    }, durationMs);

    return () => clearInterval(startInterval);
  }, [steps, currentStep, inView]);

  if (!steps?.length) {
    return null;
  }

  return (
    <BackgroundWrapper backgroundGradient={backgroundGradient}>
      <ComponentWrapper>
        <ComponentContainer
          className={backgroundGradient ? themeClasses.dark : ''}
        >
          <FlexColumn gap="40px">
            <H1 bold as="h2">
              {title}
            </H1>
            <SectionsWrapper>
              <HideDesktop>
                <MediaElement
                  aspectRatio="6/5"
                  {...currentSlide.media}
                  legacyStyles={!!backgroundGradient}
                />
              </HideDesktop>
              <FlexColumn gap="32px">
                <FlexColumn gap="32px">
                  {steps.map((step, index) => {
                    const isCurrent = index === currentStep;
                    return (
                      <StepComponent
                        key={index}
                        darkMode={!!backgroundGradient}
                        description={step.description}
                        isActive={isCurrent}
                        setActive={() => setCurrentStep(index)}
                        title={step.title}
                      />
                    );
                  })}
                  {button && (
                    <LandingButton
                      analyticsMeta={analyticsMeta}
                      isSignUp={button.isSignUp}
                      label={button.label}
                      url={button.url}
                    />
                  )}
                </FlexColumn>
              </FlexColumn>
              <HideMobile>
                <MediaElement
                  aspectRatio="6/5"
                  {...currentSlide.media}
                  legacyStyles={!!backgroundGradient}
                />
              </HideMobile>
            </SectionsWrapper>
          </FlexColumn>
        </ComponentContainer>
      </ComponentWrapper>
    </BackgroundWrapper>
  );
};
